import {
  clearBookingDraftKeys,
  clearPostCheckoutSession,
} from "../constants/bookingForm";
import {
  markReferralSignup,
  getStoredReferralCode,
  clearStoredReferralCode,
} from "../utils/referral";
import { auth } from "./config/firebase";
import {
  LINK_PENDING_CREDENTIAL_KEY,
  OAUTH_POPUP_PENDING_KEY,
  OAUTH_REDIRECT_PENDING_KEY,
  OAUTH_REDIRECT_PROVIDER_ID_KEY,
  OAUTH_ACCOUNT_LINK_PENDING_KEY,
} from "../constants/authStorage";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCustomToken,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import type {
  OAuthCredential,
  UserCredential,
} from "firebase/auth";
import { api } from "./config/api";
import { AxiosError } from "axios";
import { FirebaseError } from "firebase/app";
import { captureAppError, captureAppMessage } from "../observability/sentry";
import { capture, reset as resetPosthog } from "../analytics/safePosthog";
import { rememberAuthMethod } from "../utils/lastAuthMethod";
import { AnalyticsEvents } from "../analytics/events";

// The backend route is mounted only in local development. The frontend just
// follows the explicit flag so Playwright/dev can opt in without Firebase
// client-side rate limiting, while QA/staging/prod keep the normal flow.
const USE_DEV_EMAIL_AUTH = import.meta.env.API_USE_DEV_EMAIL_AUTH === "true";
const LINK_PENDING_CREDENTIAL_TTL_MS = 10 * 60 * 1000;
const GENERIC_LOGIN_FAILURE_MESSAGE = "Invalid email or password.";

type StoredPendingCredential = {
  createdAt: number;
  credential: unknown;
};

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isStoredPendingCredential(value: unknown): value is StoredPendingCredential {
  return (
    isRecord(value) &&
    typeof value.createdAt === "number" &&
    "credential" in value
  );
}

function storePendingCredential(credential: OAuthCredential | null) {
  if (!credential) return;

  try {
    sessionStorage.setItem(
      LINK_PENDING_CREDENTIAL_KEY,
      JSON.stringify({
        createdAt: Date.now(),
        credential: credential.toJSON(),
      }),
    );
  } catch {
    // Restricted browser storage should not block auth recovery.
  }
}

function readAndRemovePendingCredential() {
  try {
    const rawPending = sessionStorage.getItem(LINK_PENDING_CREDENTIAL_KEY);
    if (!rawPending) return null;

    sessionStorage.removeItem(LINK_PENDING_CREDENTIAL_KEY);
    const parsed = JSON.parse(rawPending) as unknown;
    const credentialJson = isStoredPendingCredential(parsed)
      ? Date.now() - parsed.createdAt <= LINK_PENDING_CREDENTIAL_TTL_MS
        ? parsed.credential
        : null
      : parsed;

    if (!credentialJson) return null;
    if (typeof credentialJson !== "string" && !isRecord(credentialJson)) {
      return null;
    }
    return OAuthProvider.credentialFromJSON(credentialJson);
  } catch {
    return null;
  }
}

/**
 * Referrals are for new homeowners only — an account that already exists is never
 * referrable, whatever link its owner followed.
 *
 * That policy used to enforce itself by omission: an existing account skips
 * /insert-homeowner entirely, so the code was simply never sent. But it was also never
 * cleared, leaving a valid code in storage that the next signup on that device would
 * pick up and misattribute. Discarding it here makes the rule explicit at the moment it
 * applies, and the event makes referral traffic landing on existing customers visible —
 * there is no referral row to count it any other way.
 */
function discardReferralCodeForExistingAccount(context: string) {
  if (!getStoredReferralCode()) return;

  clearStoredReferralCode();
  capture(AnalyticsEvents.REFERRAL_CODE_DISCARDED_EXISTING_ACCOUNT, { context });
}

async function getUserFromAzureWithRetry(attempts = 3, delayMs = 400) {
  let response = await getUserFromAzure();

  for (let attempt = 1; attempt < attempts && !response?.role; attempt++) {
    await auth.currentUser?.getIdToken(true);
    await delay(delayMs * attempt);
    response = await getUserFromAzure();
  }

  return response;
}

async function completeHomeownerLogin(opts?: { skipEmailVerifiedCheck?: boolean }) {
  const user = auth.currentUser;

  if (!user || (!opts?.skipEmailVerifiedCheck && !user.emailVerified)) {
    await signOut(auth);
    return {
      role: null,
      user: null,
      error:
        "Email not verified. Please verify your email before proceeding.",
    };
  }

  const pendingCred = readAndRemovePendingCredential();
  if (pendingCred && auth.currentUser) {
    try {
      await linkWithCredential(auth.currentUser, pendingCred);
    } catch (linkErr) {
      if (
        !(linkErr instanceof FirebaseError) ||
        linkErr.code !== "auth/provider-already-linked"
      ) {
        // Link failed — ignore, proceed with normal sign-in
      }
    }
  }

  const azureResponse = await getUserFromAzureWithRetry();

  if (!azureResponse || !azureResponse.role) {
    captureAppMessage(
      "Azure login succeeded but role missing",
      {
        area: "auth-service",
        action: "azure-login-missing-role",
        extra: {
          has_user: !!azureResponse?.user,
          has_error: Boolean(azureResponse?.error),
        },
      },
      "error",
    );

    await signOut(auth);

    return {
      role: null,
      user: null,
      error: azureResponse?.error ?? "Login failed: Please try again.",
    };
  }

  /*
   * The password form is the one thing phone mode hides, so a customer who signs in with it
   * needs to find it again next visit (KAN-621).
   *
   * Deliberately NOT recorded for OAuth (`signInWithPopup`, `signInWithGoogle`): those buttons
   * render in BOTH modes, so which form sits above them is irrelevant to a Google customer --
   * whereas overwriting a phone-first customer's preference because they once used Google would
   * push them back to a form they do not use.
   */
  rememberAuthMethod("email");

  // A successful login means the account already existed, so any code picked up from a
  // referral link in this browser can never apply to it. Dropping it here keeps the
  // email/password path symmetric with the OAuth paths — otherwise a returning Google
  // user's code is cleared while a returning email/password user's lingers until the TTL.
  discardReferralCodeForExistingAccount("login");

  return {
    role: azureResponse.role,
    user: azureResponse.user,
    error: null,
  };
}

// login function---------------------------------------------------------------
export async function homeOwnerLogin(email: string, password: string) {
  try {
    if (USE_DEV_EMAIL_AUTH) {
      // Development uses a backend-issued custom token to avoid Firebase
      // client rate limiting during repeated local and Playwright runs.
      const { data } = await api.post("/dev/auth/login", { email, password });
      await signInWithCustomToken(auth, data.customToken);
      return completeHomeownerLogin({ skipEmailVerifiedCheck: true });
    }

    await signInWithEmailAndPassword(auth, email, password);
    return completeHomeownerLogin();
  } catch (error) {
    // Capture unexpected login failures
    if (
      !(error instanceof FirebaseError) &&
      !(error instanceof AxiosError)
    ) {
      captureAppError(error, {
        area: "auth-service",
        action: "homeowner-login",
      });
    }

    await signOut(auth);

    if (error instanceof AxiosError) {
      return {
        role: null,
        user: null,
        error:
          error.response?.data?.error ??
          "Connection error. Please try again later.",
      };
    }

    if (error instanceof FirebaseError) {
      switch (error.code) {
        case "auth/too-many-requests":
          return {
            role: null,
            user: null,
            error:
              "Too many failed login attempts. Please try again later or reset your password.",
          };
        case "auth/multi-factor-auth-required":
          return {
            role: null,
            user: null,
            error:
              "Your account has additional security enabled. Please contact support to regain access.",
          };
        case "auth/user-disabled":
          return {
            role: null,
            user: null,
            error: GENERIC_LOGIN_FAILURE_MESSAGE,
          };
      }
      if (error.code.startsWith("auth/invalid-credential")) {
        try {
          const response = await api.get(
            `/email-providers/${encodeURIComponent(email)}`,
          );
          const providers: string[] = response.data.providers ?? [];
          const hasPassword = providers.includes("password");
          const oauthProviders = providers.filter((p) => p !== "password");

          if (!hasPassword && oauthProviders.length > 0) {
            const names = oauthProviders.map(providerIdToName).join(" or ");
            return {
              role: null,
              user: null,
              error: `This email is registered with ${names}. Please sign in using ${names}.`,
            };
          }

          if (providers.length === 0) {
            return {
              role: null,
              user: null,
              error:
                "No account found for this email. If you signed up recently but didn't verify your email, your account may have expired — please sign up again.",
            };
          }
        } catch {
          // email not found or network error — fall through to generic message
        }
        return {
          role: null,
          user: null,
          error: "Invalid email or password",
        };
      }
    }

    if (error instanceof Error) {
      return {
        role: null,
        user: null,
        error: error.message,
      };
    }

    return {
      role: null,
      user: null,
      error: "Connection error. Please try again later.",
    };
  }
}

function splitDisplayName(displayName: string | null): {
  firstName: string;
  lastName: string;
} {
  const name = displayName?.trim() ?? "";
  // Strip characters not allowed by backend regex (^[A-Za-z\s]+$)
  const sanitized = name.replace(/[^A-Za-z\s]/g, "").trim();

  if (!sanitized) return { firstName: "User", lastName: "User" };

  const spaceIndex = sanitized.indexOf(" ");
  if (spaceIndex === -1) return { firstName: sanitized, lastName: "User" };

  const first = sanitized.slice(0, spaceIndex).trim() || "User";
  const last = sanitized.slice(spaceIndex + 1).trim() || "User";
  return { firstName: first, lastName: last };
}

function isMobileBrowser(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function providerIdToName(providerId: string): string {
  switch (providerId) {
    case "google.com":
      return "Google";
    case "yahoo.com":
      return "Yahoo";
    case "microsoft.com":
      return "Microsoft";
    case "password":
      return "email and password";
    default:
      return providerId;
  }
}

async function buildProviderConflictMessage(
  error: unknown,
  emailOverride?: string,
): Promise<string> {
  const email =
    emailOverride ??
    (error != null &&
      typeof error === "object" &&
      "customData" in error &&
      (error as { customData?: { email?: string } }).customData?.email);

  if (email) {
    try {
      const response = await api.get(
        `/email-providers/${encodeURIComponent(email)}`,
      );
      const providers: string[] = response.data.providers ?? [];
      if (providers.length > 0) {
        const names = providers.map(providerIdToName).join(" or ");
        return `This email is already registered. Please try signing in by **${names}**.`;
      }
    } catch {
      // fall through to generic message
    }
  }
  return "An account already exists with this email using a different sign-in method.";
}

function createProviderFromId(
  providerId: string,
): GoogleAuthProvider | OAuthProvider | null {
  switch (providerId) {
    case "google.com": {
      const p = new GoogleAuthProvider();
      p.setCustomParameters({ prompt: "select_account" });
      return p;
    }
    case "microsoft.com": {
      const p = new OAuthProvider("microsoft.com");
      p.addScope("email");
      p.addScope("profile");
      p.setCustomParameters({ prompt: "select_account" });
      return p;
    }
    case "yahoo.com": {
      const p = new OAuthProvider("yahoo.com");
      p.addScope("profile");
      p.addScope("email");
      return p;
    }
    default:
      return null;
  }
}

function credentialFromError(
  error: unknown,
  provider?: GoogleAuthProvider | OAuthProvider,
) {
  if (provider instanceof GoogleAuthProvider) {
    return GoogleAuthProvider.credentialFromError(error as FirebaseError) ?? null;
  }
  // For non-Google providers use OAuthProvider extractor; fall back to Google
  // only when provider is completely unknown (defensive — shouldn't happen in practice).
  return (
    OAuthProvider.credentialFromError(error as FirebaseError) ??
    (provider === undefined
      ? GoogleAuthProvider.credentialFromError(error as FirebaseError)
      : null) ??
    null
  );
}

type OAuthLoginResult = {
  role: string | null;
  user: unknown;
  error: string | null;
  isNewUser?: boolean;
};

// Preserve the pending credential for an explicit, authenticated account-link
// flow. Never delete or replace an account based only on a submitted email.
async function resolveAccountConflict(
  conflictError: FirebaseError,
  pendingCred: OAuthCredential | null,
  action: "oauth-popup-merge" | "oauth-redirect-merge",
): Promise<OAuthLoginResult> {
  void action;
  storePendingCredential(pendingCred);
  return {
    role: null,
    user: null,
    error: await buildProviderConflictMessage(conflictError),
  };
}

async function signInWithOAuthProvider(
  provider: GoogleAuthProvider | OAuthProvider,
) {
  const isMobile = isMobileBrowser();
  // On mobile, popups are blocked by iOS Safari and Android
  // WebViews. Use redirect flow instead; the result is captured on next page
  // load via handleRedirectResult() called from AuthPage on mount.
  if (isMobile) {
    // Flag persists across the redirect so AuthProvider knows not to sign out
    // a new OAuth user whose DB record doesn't exist yet (race condition fix).
    sessionStorage.setItem(OAUTH_REDIRECT_PENDING_KEY, "1");
    sessionStorage.setItem(OAUTH_REDIRECT_PROVIDER_ID_KEY, provider.providerId);
    try {
      await signInWithRedirect(auth, provider);
      return { role: null, user: null, error: null };
    } catch (error: unknown) {
      sessionStorage.removeItem(OAUTH_REDIRECT_PENDING_KEY);
      sessionStorage.removeItem(OAUTH_REDIRECT_PROVIDER_ID_KEY);
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/operation-not-allowed":
            return {
              role: null,
              user: null,
              error:
                "This sign-in method is not enabled. Please contact support.",
            };
          case "auth/network-request-failed":
            return {
              role: null,
              user: null,
              error:
                "Network error. Please check your connection and try again.",
            };
        }
      }
      captureAppError(error, {
        area: "auth-service",
        action: "oauth-redirect-start",
      });
      return {
        role: null,
        user: null,
        error: "Connection error. Please try again later.",
      };
    }
  }

  sessionStorage.setItem(OAUTH_POPUP_PENDING_KEY, "1");
  try {
    const result = await signInWithPopup(auth, provider);
    const { user, operationType } = result;

    // Auto-link a pending credential stored from a prior conflict resolution
    const pendingCred = readAndRemovePendingCredential();
    if (pendingCred && auth.currentUser) {
      try {
        await linkWithCredential(auth.currentUser, pendingCred);
      } catch (linkErr) {
        if (
          !(linkErr instanceof FirebaseError) ||
          linkErr.code !== "auth/provider-already-linked"
        ) {
          // Link failed — ignore, proceed with normal sign-in
        }
      }
    }

    const preCheck = await getUserFromAzure();
    // Gated on a confirmed 404 (`notFound`), not just a falsy role — a 401/network
    // failure on this very first request means we don't actually know yet whether
    // the account exists, and guessing "new" there is what let a real signup
    // trigger a second, redundant insert later on (KAN-662).
    const isNewUser = operationType === "signIn" && preCheck.notFound === true;

    const { firstName, lastName } = splitDisplayName(user.displayName);
    const email = user.email ?? "";

    // An existing account is never referrable, and it skips the save below entirely —
    // so this is the only point the code would otherwise be left behind.
    if (!isNewUser) discardReferralCodeForExistingAccount("oauth-popup");

    // Insert into DB for new users (or upsert — backend has ON CONFLICT DO UPDATE)
    if (isNewUser) {
      const saveResponse = await saveHomeownerToAzure(
        email,
        firstName,
        lastName,
        user.uid,
        undefined,
        true,
      );
      if (saveResponse.error) {
        await signOut(auth);
        return {
          role: null,
          user: null,
          error: "Failed to save account. Please try again.",
        };
      }
      // Force-refresh the ID token so the next authenticated request
      // (getUserFromAzure) carries a valid, non-stale Bearer token.
      await auth.currentUser?.getIdToken(true);
    }

    const azureResponse = await getUserFromAzureWithRetry(3);

    if (!azureResponse?.role) {
      if (!azureResponse?.notFound) {
        // Every attempt failed on auth, not a confirmed absence — most often a
        // freshly-minted OAuth token that hasn't finished propagating to the
        // backend's Firebase Admin SDK yet. The account almost certainly exists
        // (the insert above already succeeded); inserting again here is exactly
        // the KAN-662 double-insert, and used to also report a false "Login
        // failed" for a signup that had actually worked.
        captureAppMessage(
          "OAuth signup: user check kept failing auth, not re-inserting",
          { area: "auth-service", action: "oauth-popup-auth-error" },
        );
        await signOut(auth);
        return {
          role: null,
          user: null,
          error: isNewUser
            ? "Your account was created. Please try logging in again in a moment."
            : "Login failed: Please try again.",
        };
      }

      // Confirmed absence, not an auth hiccup — the insert above never actually
      // landed (or isNewUser was wrong). Safe to insert again.
      captureAppMessage(
        "OAuth signup: retrying insert after confirmed not-found",
        { area: "auth-service", action: "oauth-popup-retry-insert" },
      );
      const saveResponse = await saveHomeownerToAzure(email, firstName, lastName, user.uid, undefined, true);
      if (saveResponse.error) {
        await signOut(auth);
        return { role: null, user: null, error: "Failed to save account. Please try again." };
      }
      const retryResponse = await getUserFromAzureWithRetry(2);
      if (!retryResponse?.role) {
        await signOut(auth);
        return { role: null, user: null, error: "Login failed: Please try again." };
      }
      return { role: retryResponse.role, user: retryResponse.user, error: null, isNewUser: true };
    }

    return { role: azureResponse.role, user: azureResponse.user, error: null, isNewUser };
  } catch (error) {
    if (!(error instanceof FirebaseError) || !error.code?.startsWith("auth/")) {
      captureAppError(error, {
        area: "auth-service",
        action: "oauth-popup",
      });
    }
    await signOut(auth);

    if (error instanceof FirebaseError) {
      switch (error.code) {
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
          return { role: null, user: null, error: null }; // user cancelled, not an error
        case "auth/account-exists-with-different-credential": {
          const pendingCred = credentialFromError(error, provider);
          return resolveAccountConflict(error, pendingCred, "oauth-popup-merge");
        }
        case "auth/operation-not-allowed":
          return {
            role: null,
            user: null,
            error:
              "This sign-in method is not enabled. Please contact support.",
          };
        case "auth/popup-blocked":
          return {
            role: null,
            user: null,
            error:
              "Sign-in popup was blocked by your browser. Please allow popups and try again.",
          };
        case "auth/web-storage-unsupported":
          return {
            role: null,
            user: null,
            error:
              "Your browser does not support this sign-in method. Please try a different browser.",
          };
        case "auth/network-request-failed":
          return {
            role: null,
            user: null,
            error: "Network error. Please check your connection and try again.",
          };
        case "auth/multi-factor-auth-required":
          return {
            role: null,
            user: null,
            error:
              "Your account has additional security enabled. Please contact support to regain access.",
          };
        case "auth/user-disabled":
          return {
            role: null,
            user: null,
            error: GENERIC_LOGIN_FAILURE_MESSAGE,
          };
        case "auth/requires-recent-login":
          return {
            role: null,
            user: null,
            error: "For security, please sign in again before continuing.",
          };
        case "auth/invalid-tenant-id":
          return {
            role: null,
            user: null,
            error: "Sign-in configuration error. Please contact support.",
          };
      }
    }
    return {
      role: null,
      user: null,
      error: "Connection error. Please try again later.",
    };
  } finally {
    sessionStorage.removeItem(OAUTH_POPUP_PENDING_KEY);
  }
}

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  // Always show account picker so users are not silently auto-logged
  // in with the wrong Google account.
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithOAuthProvider(provider);
}

export async function signInWithMicrosoft() {
  const provider = new OAuthProvider("microsoft.com");
  provider.addScope("openid"); // required for OIDC ID token from Microsoft
  provider.addScope("email");
  provider.addScope("profile");
  // Allow both personal and work/school Microsoft accounts and
  // always show the account picker.
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithOAuthProvider(provider);
}

export async function signInWithYahoo() {
  const provider = new OAuthProvider("yahoo.com");
  provider.addScope("profile");
  provider.addScope("email");
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithOAuthProvider(provider);
}

type OAuthProviderId = "google.com" | "microsoft.com" | "yahoo.com";

/**
 * Tells the backend to write the credential audit row for a link that just succeeded (KAN-626).
 *
 * DELIBERATELY NEVER THROWS, and never changes what the caller reports to the customer. Firebase
 * has already linked the credential by this point, so a failed audit write does not undo anything
 * -- surfacing it as a failed link would be a false statement about their account. The gap is
 * reported to Sentry instead, because an audit row that silently never arrives is exactly the one
 * support will be missing months later when asked who added a sign-in method.
 *
 * The backend re-checks the provider against Firebase before writing, so this call is a trigger,
 * not a trusted claim.
 */
async function recordProviderLinkAudit(providerId: OAuthProviderId): Promise<void> {
  try {
    await api.post("/record-provider-link", { providerId });
  } catch (error) {
    captureAppError(error, {
      area: "account-security",
      action: "record-provider-link-audit",
      tags: { providerId },
      extra: { note: "provider linked in Firebase but the audit row may be missing" },
    });
  }
}

/**
 * Adds an OAuth sign-in method to the already authenticated Firebase user (KAN-622).
 *
 * This is credential linking, not a second sign-in: the current UID stays in place, so bookings,
 * payment methods, and the homeowner record remain attached to the same account. A provider that
 * belongs to another UID is deliberately refused; joining accounts is a support-led operation.
 */
export async function linkOAuthProvider(providerId: OAuthProviderId): Promise<{
  error: string | null;
  redirecting: boolean;
}> {
  const user = auth.currentUser;
  if (!user) {
    return { error: "Please sign in again before adding a sign-in method.", redirecting: false };
  }

  if (user.providerData.some((item) => item.providerId === providerId)) {
    return { error: "That sign-in method is already connected.", redirecting: false };
  }

  const provider = createProviderFromId(providerId);
  if (!provider) {
    return { error: "This sign-in method is unavailable. Please contact support.", redirecting: false };
  }

  try {
    if (isMobileBrowser()) {
      sessionStorage.setItem(OAUTH_ACCOUNT_LINK_PENDING_KEY, providerId);
      await linkWithRedirect(user, provider);
      return { error: null, redirecting: true };
    }

    await linkWithPopup(user, provider);
    await recordProviderLinkAudit(providerId);
    return { error: null, redirecting: false };
  } catch (error) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case "auth/credential-already-in-use":
        case "auth/email-already-in-use":
          /*
           * Reported, even though the refusal is working correctly (KAN-626).
           *
           * This is the ONLY branch here that sends the customer to a human. Without a signal, the
           * first anyone hears of it is the support email, with no record of which provider or
           * when -- and MVP has no automated merge, so every one of these is manual work someone
           * has to do. A warning, not an error: the guard did its job.
           */
          captureAppMessage("OAuth link refused - credential belongs to another account", {
            area: "account-security",
            action: "link-oauth-provider",
            tags: { providerId, firebaseCode: error.code },
          });
          return {
            error:
              "That sign-in method belongs to a different account. Please contact support to combine your accounts.",
            redirecting: false,
          };
        case "auth/requires-recent-login":
          return {
            error: "For security, please sign in again before adding a sign-in method.",
            redirecting: false,
          };
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
          return { error: null, redirecting: false };
        case "auth/popup-blocked":
          return {
            error: "Your browser blocked the sign-in window. Please allow popups and try again.",
            redirecting: false,
          };
        case "auth/operation-not-allowed":
          /*
           * OUR misconfiguration, not the customer's problem: the provider is not enabled in the
           * Firebase console. It fails identically for every customer who tries it, and the button
           * is on the page regardless -- so without this the feature is simply broken for one
           * provider and the only symptom is customers quietly giving up. Error level, because
           * something is wrong on our side and wants fixing today.
           */
          captureAppError(error, {
            area: "account-security",
            action: "link-oauth-provider",
            tags: { providerId, firebaseCode: error.code },
            extra: { note: "provider is not enabled in the Firebase console" },
          });
          return {
            error: "This sign-in method is not enabled. Please contact support.",
            redirecting: false,
          };
      }
    }

    captureAppError(error, { area: "account-security", action: "link-oauth-provider" });
    return { error: "We couldn't add that sign-in method. Please try again.", redirecting: false };
  }
}

/** Completes an Account Settings OAuth-link redirect after the browser returns on mobile. */
export async function completeOAuthLinkRedirect(): Promise<{
  providerId: string | null;
  error: string | null;
} | null> {
  const providerId = sessionStorage.getItem(OAUTH_ACCOUNT_LINK_PENDING_KEY);
  if (!providerId) return null;

  try {
    const result = await getRedirectResult(auth);
    if (!result) {
      return { providerId, error: "We couldn't complete the sign-in link. Please try again." };
    }
    // Same audit row as the popup path -- mobile completes the link through a redirect, and an
    // account linked on a phone must not end up with a thinner trail than one linked on a laptop.
    await recordProviderLinkAudit(providerId as OAuthProviderId);
    return { providerId, error: null };
  } catch (error) {
    if (error instanceof FirebaseError && error.code === "auth/credential-already-in-use") {
      // Same event as the popup path, same reason for reporting it -- a collision on a phone is no
      // less a support ticket than one on a laptop.
      captureAppMessage("OAuth link refused - credential belongs to another account", {
        area: "account-security",
        action: "complete-oauth-link",
        tags: { providerId, firebaseCode: error.code },
      });
      return {
        providerId,
        error:
          "That sign-in method belongs to a different account. Please contact support to combine your accounts.",
      };
    }
    captureAppError(error, { area: "account-security", action: "complete-oauth-link" });
    return { providerId, error: "We couldn't add that sign-in method. Please try again." };
  } finally {
    sessionStorage.removeItem(OAUTH_ACCOUNT_LINK_PENDING_KEY);
  }
}

// Captures the Firebase auth result after a signInWithRedirect flow
// completes on mobile. Called on page mount from AuthPage. Returns the same
// shape as signInWithOAuthProvider so callers can navigate on success.
export async function handleRedirectResult(): Promise<{
  role: string | null;
  user: unknown;
  error: string | null;
  isNewUser?: boolean;
} | null> {
  try {
    const result = await Promise.race([
      getRedirectResult(auth),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("oauth_redirect_timeout")),
          6_000,
        ),
      ),
    ]);
    if (!result) {
      // No redirect pending (desktop popup flow) — clear the flag and exit.
      sessionStorage.removeItem(OAUTH_REDIRECT_PENDING_KEY);
      return null;
    }

    // Path B: after signing in with existing provider to resolve a conflict,
    // a serialised pending credential is waiting in sessionStorage. Link it
    // silently now that we have a signed-in currentUser.
    const pendingCred = readAndRemovePendingCredential();
    if (pendingCred && auth.currentUser) {
      try {
        try {
          await linkWithCredential(auth.currentUser, pendingCred);
        } catch (linkErr) {
          if (
            !(linkErr instanceof FirebaseError) ||
            linkErr.code !== "auth/provider-already-linked"
          ) {
            throw linkErr;
          }
        }
      } catch {
        // Linking failed — user is still signed in with the existing provider,
        // so fall through and let them reach the dashboard. They can re-link later.
      }
    }

    const { user, operationType } = result;
    const preCheck = await getUserFromAzure();
    // Gated on a confirmed 404 (`notFound`), not just a falsy role — a 401/network
    // failure on this very first request means we don't actually know yet whether
    // the account exists, and guessing "new" there is what let a real signup
    // trigger a second, redundant insert later on (KAN-662).
    const isNewUser = operationType === "signIn" && preCheck.notFound === true;

    const { firstName, lastName } = splitDisplayName(user.displayName);
    const email = user.email ?? "";

    // Same rule on the mobile redirect path as on the popup path above.
    if (!isNewUser) discardReferralCodeForExistingAccount("oauth-redirect");

    if (isNewUser) {
      const saveResponse = await saveHomeownerToAzure(
        email,
        firstName,
        lastName,
        user.uid,
        undefined,
        true,
      );
      if (saveResponse.error) {
        await signOut(auth);
        return {
          role: null,
          user: null,
          error: "Failed to save account. Please try again.",
        };
      }
      // Force-refresh the ID token so the next authenticated request
      // (getUserFromAzure) carries a valid, non-stale Bearer token.
      await auth.currentUser?.getIdToken(true);
    }

    const azureResponse = await getUserFromAzureWithRetry(3);

    if (!azureResponse?.role) {
      if (!azureResponse?.notFound) {
        // See the popup flow's identical branch above — an auth error here almost
        // certainly means the account already exists (KAN-662), not that it's missing.
        captureAppMessage(
          "OAuth signup: user check kept failing auth, not re-inserting",
          { area: "auth-service", action: "oauth-redirect-auth-error" },
        );
        await signOut(auth);
        return {
          role: null,
          user: null,
          error: isNewUser
            ? "Your account was created. Please try logging in again in a moment."
            : "Login failed: Please try again.",
        };
      }

      // Confirmed absence, not an auth hiccup — safe to insert again.
      captureAppMessage(
        "OAuth signup: retrying insert after confirmed not-found",
        { area: "auth-service", action: "oauth-redirect-retry-insert" },
      );
      const saveRetry = await saveHomeownerToAzure(email, firstName, lastName, user.uid, undefined, true);
      if (saveRetry.error) {
        await signOut(auth);
        return { role: null, user: null, error: "Failed to save account. Please try again." };
      }
      const retryResponse = await getUserFromAzureWithRetry(2);
      if (!retryResponse?.role) {
        await signOut(auth);
        return { role: null, user: null, error: "Login failed: Please try again." };
      }
      return { role: retryResponse.role, user: retryResponse.user, error: null, isNewUser: true };
    }

    return { role: azureResponse.role, user: azureResponse.user, error: null, isNewUser };
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case "auth/account-exists-with-different-credential": {
          const redirectProviderId = sessionStorage.getItem(OAUTH_REDIRECT_PROVIDER_ID_KEY) ?? "";
          sessionStorage.removeItem(OAUTH_REDIRECT_PROVIDER_ID_KEY);
          const redirectProvider = createProviderFromId(redirectProviderId) ?? undefined;
          const pendingCred = credentialFromError(error, redirectProvider);
          return resolveAccountConflict(error, pendingCred, "oauth-redirect-merge");
        }
        case "auth/multi-factor-auth-required":
          return {
            role: null,
            user: null,
            error:
              "Your account has additional security enabled. Please contact support to regain access.",
          };
        case "auth/user-disabled":
          return {
            role: null,
            user: null,
            error: GENERIC_LOGIN_FAILURE_MESSAGE,
          };
        case "auth/network-request-failed":
          return {
            role: null,
            user: null,
            error: "Network error. Please check your connection and try again.",
          };
        case "auth/user-cancelled":
          // User dismissed the IdP consent screen — not an app error, silently dismiss.
          return null;
      }
    }
    if (error instanceof Error && error.message === "oauth_redirect_timeout") {
      // User pressed back before completing OAuth — silently dismiss the spinner.
      return null;
    }
    captureAppError(error, {
      area: "auth-service",
      action: "oauth-redirect-result",
    });
    return {
      role: null,
      user: null,
      error: "Connection error. Please try again later.",
    };
  } finally {
    // Always clear the flag when the function exits so AuthProvider
    // resumes normal sign-out logic after the redirect flow completes.
    sessionStorage.removeItem(OAUTH_REDIRECT_PENDING_KEY);
  }
}

export async function loginStaff() {
  // Precondition: User must be signed into Firebase (via staffLoginService)
  const response = await getUserFromAzure();
  if (!response.error) {
    return { response: "log in successful.", error: null };
  } else {
    await signOut(auth);
    return { response: null, error: response.error };
  }
}

// signup function---------------------------------------------------------------
export async function SignUpHomeOwner(
  email: string,
  firstName: string,
  lastName: string,
  password: string,
) {
  if (USE_DEV_EMAIL_AUTH) {
    try {
      const referralCode = getStoredReferralCode() || undefined;

      const { data } = await api.post("/dev/auth/signup", {
        email,
        firstName,
        lastName,
        password,
        referralCode,
      });

      if (referralCode) {
        clearStoredReferralCode();
        markReferralSignup();
      }

      return {
        data: data.message ?? `Signup successful! A verification email has been sent to ${email}. Please verify before logging in.`,
        error: null,
      };
    } catch (err) {
      if (err instanceof AxiosError) {
        return {
          data: "Signup failed",
          error:
            err.response?.data?.error ??
            "Connection error. Please try again later",
        };
      }

      captureAppError(err, {
        area: "auth-service",
        action: "signup-dev-email",
      });
      return {
        data: "Signup failed",
        error:
          err instanceof Error
            ? err.message
            : "Failed to sign up. Please try again later.",
      };
    }
  }

  let userCredentials: UserCredential | null = null;
  try {
    // create Firebase user
    userCredentials = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const firebaseUser = userCredentials.user;

    // save user to db and send email verification, if fail rollback firebase and db
    const azureResponse = await saveHomeownerToAzure(
      email,
      firstName,
      lastName,
      firebaseUser.uid,
    );

    if (azureResponse.error) {
      // Backend may have already rolled back the Firebase user; try delete as safety net
      try {
        await userCredentials.user.delete();
      } catch {
        // Already deleted by backend rollback — clear the local Firebase session
        await signOut(auth).catch(() => {});
      }
      return { data: "Signup failed", error: azureResponse.error };
    }

    // Sign out the user from firebase, such that user must login.
    await signOutUser();

    // return success message
    return {
      data: `Signup successful! A verification email has been sent to ${email}. Please verify before logging in.`,
      error: null,
    };
  } catch (err) {
    // Clean up Firebase user if it was created before the unexpected error
    if (userCredentials?.user) {
      try {
        await userCredentials.user.delete();
      } catch {
        // Ignore cleanup failure so the original signup error can be handled.
      }
    }

    // Expected Firebase auth errors — do not send to Sentry
    if (err instanceof FirebaseError) {
      switch (err.code) {
        case "auth/email-already-in-use":
          return {
            data: "Signup failed",
            error: await buildProviderConflictMessage(err, email),
          };
        case "auth/invalid-email":
          return {
            data: "Signup failed",
            error:
              "Invalid email. Please check your credentials and try again.",
          };
        case "auth/weak-password":
          return {
            data: "Signup failed",
            error: "Password is too weak. Use at least 6 characters.",
          };
        case "auth/network-request-failed":
          return {
            data: "Signup failed",
            error:
              "Couldn't reach the server. Please check your internet connection and try again.",
          };
        default:
          return { data: "Signup failed", error: err.message || err.code };
      }
    }

    captureAppError(err, {
      area: "auth-service",
      action: "signup-homeowner",
    });
    return {
      data: "Signup failed",
      error: "Failed to sign up. Please try again later.",
    };
  }
}

// insert user to db function
async function saveHomeownerToAzure(
  email: string,
  firstName: string,
  lastName: string,
  firebaseId: string,
  stripeCustomerId?: string,
  verified = false,
  explicitReferralCode?: string,
) {
  try {
    // Prefer the code the caller validated in this flow; fall back to storage for the
    // paths that don't thread it through. Reading only from storage meant a blocked or
    // cleared localStorage silently dropped an already-validated code.
    const referralCode =
      explicitReferralCode || getStoredReferralCode() || undefined;

    await api.post("/insert-homeowner", {
      firstName,
      lastName,
      email,
      firebase_id: firebaseId,
      stripe_customer_id: stripeCustomerId || null,
      verified,
      created_at: new Date().toISOString(),
      referralCode,
    });

    if (referralCode) {
      clearStoredReferralCode();
      markReferralSignup();
    }

    return { data: "Saved to backend successfully", error: null };
  } catch (err) {
    if (err instanceof AxiosError) {
      const serverError = err.response?.data?.error;
      if (serverError) {
        // Don't Sentry 422 (invalid email — expected user error)
        if (err.response?.status !== 422) {
          captureAppError(err, {
            area: "auth-service",
            action: "insert-homeowner",
          });
        }
        return { data: "Signup failed", error: serverError };
      }
    }
    captureAppError(err, {
      area: "auth-service",
      action: "insert-homeowner",
    });
    return {
      data: "Signup failed",
      error: "Connection error. Please try again later",
    };
  }
}

// Helper to fetch user role/data from backend
//
// `notFound` and `authError` let callers tell "this homeowner genuinely has no
// row yet" (404) apart from "the token was rejected" (401) — the response
// interceptor already retries a 401 once with a force-refreshed token, so
// reaching here on a 401 means that retry ALSO failed, most often a brand-new
// OAuth token that hasn't finished propagating on the backend's Firebase
// Admin SDK yet. Collapsing both into one "not found" shape (the previous
// behaviour) made a signup that actually succeeded look like it needed to be
// retried from scratch (KAN-662).
export async function getUserFromAzure() {
  try {
    const response = await api.get("/user");
    return {
      user: response.data.user,
      role: response.data.role,
      error: null,
      notFound: false,
      authError: false,
    };
  } catch (err) {
    // 404 = expected for new OAuth users during pre-check; do not send to Sentry
    // 401 = stale token recovered by response interceptor retry; if retry also
    //       fails the session is genuinely invalid — sign out, not a Sentry event
    const is404 = err instanceof AxiosError && err.response?.status === 404;
    const is401 = err instanceof AxiosError && err.response?.status === 401;
    if (!is404 && !is401) {
      captureAppError(err, {
        area: "auth-service",
        action: "get-user-from-azure",
      });
    }

    if (err instanceof AxiosError) {
      switch (err.code) {
        case "ECONNABORTED":
          return {
            error: "Network error. Please try again later.",
            role: null,
            notFound: false,
            authError: false,
          };
        case "ERR_NETWORK":
          return {
            error: "Network error. Please try again later.",
            role: null,
            notFound: false,
            authError: false,
          };
        default:
          return {
            error:
              "User not found. Please check your credentials before trying again.",
            role: null,
            notFound: is404,
            authError: is401,
          };
      }
    }
    return {
      error: "Network error. Please try again later.",
      role: null,
      notFound: false,
      authError: false,
    };
  }
}

// signout function
export async function signOutUser(): Promise<{
  data: string | null;
  error: string | null;
}> {
  try {
    await signOut(auth);
    clearPostCheckoutSession();
    clearBookingDraftKeys();
    resetPosthog();
    return { data: "Successfully signed out!", error: null };
  } catch {
    return { data: null, error: "Failed to sign out!" };
  }
}

export async function recordLogin(method: string): Promise<void> {
  try {
    await api.post("/track-login", { method });
  } catch {
    // analytics only — never surfaces to user
  }
}

// ── Magic link (passwordless email) ──────────────────────────────────────────

export const MAGIC_LINK_EMAIL_KEY = "hh_magic_link_email";

export async function sendMagicLinkEmail(
  email: string,
): Promise<{ error: string | null; errorCode?: string }> {
  try {
    await api.post("/send-magic-link", { email });
    try {
      localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
    } catch {
      // restricted storage — user must re-enter email on the action page
    }
    return { error: null };
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response?.status === 400) {
        return { error: "Please enter a valid email address." };
      }
      if (err.response?.status === 403) {
        return {
          error: "Your email is not verified yet. Please verify your email first.",
          errorCode: "EMAIL_NOT_VERIFIED",
        };
      }
      if (err.response?.status === 409) {
        return {
          error: err.response.data?.error ?? "This account uses a different sign-in method.",
          errorCode: "OAUTH_ONLY_ACCOUNT",
        };
      }
      if (err.response?.status === 429) {
        return { error: "Too many requests. Please try again later." };
      }
    }
    captureAppError(err, { area: "auth-service", action: "send-magic-link" });
    return { error: "Failed to send login link. Please try again." };
  }
}

export function isMagicLinkUrl(url: string): boolean {
  return isSignInWithEmailLink(auth, url);
}

export async function signInWithMagicLink(
  email: string,
  url: string,
): Promise<{ error: string | null; errorCode?: string }> {
  try {
    await signInWithEmailLink(auth, email, url);
    try {
      localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    } catch {
      // best-effort cleanup
    }
    // Requested from the email form, and reachable only from there, so it counts as "email".
    rememberAuthMethod("email");
    return { error: null };
  } catch (err: unknown) {
    if (err instanceof FirebaseError) {
      if (err.code === "auth/expired-action-code") {
        return { error: "This login link has expired. Please request a new one.", errorCode: "expired" };
      }
      if (err.code === "auth/invalid-action-code") {
        return { error: "This login link has already been used.", errorCode: "used" };
      }
      if (err.code === "auth/invalid-email") {
        return { error: "Email address doesn't match. Please enter the email you used to request the link.", errorCode: "email_mismatch" };
      }
    }
    captureAppError(err, { area: "auth-service", action: "signin-magic-link" });
    return { error: "Failed to sign in. Please try again.", errorCode: "error" };
  }
}
